const API_BASE = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5001/api';

function getAuthToken() {
  return localStorage.getItem('e3_auth_token');
}

async function request(url, options = {}) {
  const token = getAuthToken();
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const res = await fetch(`${API_BASE}${url}`, { ...options, headers });
  if (res.status === 401) {
    // Token expired or invalid — clear it
    localStorage.removeItem('e3_auth_token');
    localStorage.removeItem('e3_user');
    window.dispatchEvent(new Event('auth-expired'));
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Request failed');
  }
  if (res.status === 204) return null;
  return res.json();
}

// Auth
export const checkSetup = () => request('/auth/check-setup');
export const setupAdmin = (data) => request('/auth/setup', { method: 'POST', body: JSON.stringify(data) });
export const login = (data) => request('/auth/login', { method: 'POST', body: JSON.stringify(data) });
export const getMe = () => request('/auth/me');
export const getUsers = () => request('/auth/users');
export const createUser = (data) => request('/auth/users', { method: 'POST', body: JSON.stringify(data) });
export const updateUser = (id, data) => request(`/auth/users/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const getPMList = () => request('/auth/pm-list');
export const getRoles = () => request('/auth/roles');
export const getOrgTree = () => request('/auth/org-tree');
export const getSubordinates = (userId) => request(`/auth/subordinates/${userId}`);

// Projects
export const getProjects = () => request('/projects');
export const getProject = (id) => request(`/projects/${id}`);
export const getProjectSummary = (id) => request(`/projects/${id}/summary`);
export const createProject = (data) => request('/projects', { method: 'POST', body: JSON.stringify(data) });
export const updateProject = (id, data) => request(`/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteProject = (id) => request(`/projects/${id}`, { method: 'DELETE' });
export const uploadProjectLogo = (id, logo_url) => request(`/projects/${id}/logo`, { method: 'PUT', body: JSON.stringify({ logo_url }) });

// SOV Lines
export const getSOVLines = (projectId) => request(`/sov-lines/project/${projectId}`);
export const getSOVLine = (id) => request(`/sov-lines/${id}`);
export const createSOVLine = (data) => request('/sov-lines', { method: 'POST', body: JSON.stringify(data) });
export const updateSOVLine = (id, data) => request(`/sov-lines/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteSOVLine = (id) => request(`/sov-lines/${id}`, { method: 'DELETE' });
export const checkSOVAllocation = (projectId) => request(`/sov-lines/project/${projectId}/allocation-check`);
export const rollupSOVLine = (id) => request(`/sov-lines/${id}/rollup`, { method: 'POST' });
export const getRollupSummary = (projectId) => request(`/sov-lines/project/${projectId}/rollup-summary`);
export const resetSOVProgress = (projectId) => request(`/sov-lines/project/${projectId}/reset-progress`, { method: 'POST' });
export const getSOVChangeLog = (id) => request(`/sov-lines/${id}/change-log`);
export const getSOVProjectChangeLog = (projectId) => request(`/sov-lines/project/${projectId}/change-log`);

// Tasks
export const getTasks = (projectId) => request(`/tasks/project/${projectId}`);
export const getTask = (id) => request(`/tasks/${id}`);
export const createTask = (data) => request('/tasks', { method: 'POST', body: JSON.stringify(data) });
export const updateTask = (id, data) => request(`/tasks/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteTask = (id) => request(`/tasks/${id}`, { method: 'DELETE' });
export const getGanttData = (projectId) => request(`/tasks/project/${projectId}/gantt`);
export const addPredecessor = (taskId, data) => request(`/tasks/${taskId}/predecessors`, { method: 'POST', body: JSON.stringify(data) });
export const updatePredecessor = (taskId, predId, data) => request(`/tasks/${taskId}/predecessors/${predId}`, { method: 'PUT', body: JSON.stringify(data) });
export const removePredecessor = (taskId, predId) => request(`/tasks/${taskId}/predecessors/${predId}`, { method: 'DELETE' });
export const getDependencyAlerts = (projectId) => request(`/tasks/project/${projectId}/dependency-alerts`);
export const getAllDependencyAlerts = () => request('/tasks/all-dependency-alerts');

// Sections
export const getSections = (projectId) => request(`/sections/project/${projectId}`);
export const createSection = (data) => request('/sections', { method: 'POST', body: JSON.stringify(data) });
export const updateSection = (id, data) => request(`/sections/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteSection = (id) => request(`/sections/${id}`, { method: 'DELETE' });
export const reorderSections = (projectId, order) => request(`/sections/project/${projectId}/reorder`, { method: 'PUT', body: JSON.stringify({ order }) });

// Allocations
export const getAllocations = (projectId) => request(`/allocations/project/${projectId}`);
export const getSOVAllocations = (sovLineId) => request(`/allocations/sov/${sovLineId}`);
export const getTaskAllocations = (taskId) => request(`/allocations/task/${taskId}`);
export const createAllocation = (data) => request('/allocations', { method: 'POST', body: JSON.stringify(data) });
export const updateAllocation = (id, data) => request(`/allocations/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteAllocation = (id) => request(`/allocations/${id}`, { method: 'DELETE' });

// Billing
export const getBillingPeriods = (projectId) => request(`/billing/project/${projectId}`);
export const getBillingPeriod = (id) => request(`/billing/${id}`);
export const createBillingPeriod = (data) => request('/billing', { method: 'POST', body: JSON.stringify(data) });
export const updateBillingPeriod = (id, data) => request(`/billing/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteBillingPeriod = (id) => request(`/billing/${id}`, { method: 'DELETE' });
export const generateBillingLines = (id) => request(`/billing/${id}/generate-lines`, { method: 'POST' });
export const updateBillingLine = (lineId, data) => request(`/billing/lines/${lineId}`, { method: 'PUT', body: JSON.stringify(data) });
export const approveBilling = (id) => request(`/billing/${id}/approve`, { method: 'POST' });
export const lockBilling = (id) => request(`/billing/${id}/lock`, { method: 'POST' });
export const advanceBillingStatus = (id) => request(`/billing/${id}/advance-status`, { method: 'POST' });
export const createRevisedBillingPeriod = (id) => request(`/billing/${id}/create-revised`, { method: 'POST' });
export const getScheduleComparison = (id) => request(`/billing/${id}/schedule-comparison`);
export const getSchedulePerformance = (projectId) => request(`/billing/project/${projectId}/schedule-performance`);
export const getBillingPerformance = (projectId) => request(`/billing/project/${projectId}/billing-performance`);
export const refreshFromSchedule = (id) => request(`/billing/${id}/refresh-from-schedule`, { method: 'POST' });
export const getProgressAlerts = (id) => request(`/billing/${id}/progress-alerts`);
export const getProgressAlertsSummary = (projectId) => request(`/billing/project/${projectId}/progress-alerts-summary`);
export const getAllProjectsAlerts = () => request('/billing/all-projects-alerts');

// Accounts Receivable
export const getAccountsReceivable = () => request('/ar');

// Schedule Change Log
export const getScheduleChangeLog = (projectId, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/schedule-log/project/${projectId}${qs ? '?' + qs : ''}`);
};
export const getScheduleChangeSummary = (projectId) => request(`/schedule-log/project/${projectId}/summary`);
export const getTaskHistory = (projectId, taskId) => request(`/schedule-log/project/${projectId}/task/${taskId}/history`);
export const getScheduleLogPMs = () => request('/schedule-log/pms');
export const getPMScheduleSummary = (pmName) => request(`/schedule-log/pm/${encodeURIComponent(pmName)}/summary`);

// Change Orders
export const getChangeOrders = (projectId) => request(`/change-orders/project/${projectId}`);
export const getChangeOrder = (id) => request(`/change-orders/${id}`);
export const createChangeOrder = (data) => request('/change-orders', { method: 'POST', body: JSON.stringify(data) });
export const updateChangeOrder = (id, data) => request(`/change-orders/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteChangeOrder = (id) => request(`/change-orders/${id}`, { method: 'DELETE' });

// Contract Value Log
export const getContractValueLog = (projectId) => request(`/contract-value-log/project/${projectId}`);
export const getContractValueSummary = (projectId) => request(`/contract-value-log/project/${projectId}/summary`);
export const createManualAdjustment = (data) => request('/contract-value-log/manual-adjustment', { method: 'POST', body: JSON.stringify(data) });

// Forecast
export const getForecasts = (projectId) => request(`/forecast/project/${projectId}`);
export const createForecast = (data) => request('/forecast', { method: 'POST', body: JSON.stringify(data) });
export const updateForecast = (id, data) => request(`/forecast/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteForecast = (id) => request(`/forecast/${id}`, { method: 'DELETE' });
export const autoGenerateForecast = (projectId) => request(`/forecast/project/${projectId}/auto-generate`, { method: 'POST' });
export const getForecastDashboard = (projectId) => request(`/forecast/project/${projectId}/dashboard`);

// Retainage Release
export const releaseRetainage = (sovLineId, data) => request(`/sov-lines/${sovLineId}/release-retainage`, { method: 'POST', body: JSON.stringify(data) });
export const unreleaseRetainage = (sovLineId, data) => request(`/sov-lines/${sovLineId}/unrelease-retainage`, { method: 'POST', body: JSON.stringify(data) });

// Workload
export const getWorkloadDashboard = () => request('/workload/dashboard');
export const updatePMCapacity = (pm_name, capacity_cap) => request('/workload/pm-cap', { method: 'PUT', body: JSON.stringify({ pm_name, capacity_cap }) });

// Exports
export const getExportLogs = (projectId) => request(`/exports/project/${projectId}`);
export const generateBillingExport = (data) => request('/exports/billing-export', { method: 'POST', body: JSON.stringify(data) });
export const generateClientSchedule = (data) => request('/exports/client-schedule', { method: 'POST', body: JSON.stringify(data) });

// Audit Log
export const getAuditLog = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/audit-log${qs ? '?' + qs : ''}`);
};
export const getAuditSummary = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/audit-log/summary${qs ? '?' + qs : ''}`);
};

// Sync — CE Financial Data
export const triggerSyncJobCost = (data) => request('/sync/job-cost', { method: 'POST', body: JSON.stringify(data) });
export const triggerSyncAPAR = (data) => request('/sync/ap-ar', { method: 'POST', body: JSON.stringify(data) });
export const getSyncJobStatus = (ceJobId) => request(`/sync/job/${ceJobId}`);
export const getSyncHistory = (limit = 20) => request(`/sync/history?limit=${limit}`);
export const getLastSync = () => request('/sync/last-sync');
export const getSyncJobCosts = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/sync/job-costs${qs ? `?${qs}` : ''}`);
};
export const getSyncJobCostSummary = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/sync/job-costs/summary${qs ? `?${qs}` : ''}`);
};

// Cumulative (to-date) job cost data
export const getSyncJobCostsCurrent = (jobId) => request(`/sync/job-costs/current?job_id=${encodeURIComponent(jobId)}`);
export const getSyncJobCostCurrentSummary = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/sync/job-costs/current/summary${qs ? `?${qs}` : ''}`);
};

// Monthly breakdown
export const getSyncJobCostsMonthly = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/sync/job-costs/monthly${qs ? `?${qs}` : ''}`);
};
export const getSyncJobCostMonthlySummary = (jobId) => request(`/sync/job-costs/monthly/summary?job_id=${encodeURIComponent(jobId)}`);

// Phase drill-down & period list
export const getSyncJobCostPhases = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/sync/job-costs/phases${qs ? `?${qs}` : ''}`);
};
export const getSyncJobCostPeriods = (jobId) => request(`/sync/job-costs/periods?job_id=${encodeURIComponent(jobId)}`);

export const getSyncAPAR = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/sync/ap-ar${qs ? `?${qs}` : ''}`);
};
export const getSyncARaging = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return request(`/sync/ap-ar/aging${qs ? `?${qs}` : ''}`);
};
export const getSyncDashboard = (jobId) => request(`/sync/dashboard/${jobId}`);
